import { useApolloClient, useSuspenseQuery } from '@apollo/client';
import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { Button, Separator, Stack, Text, toast } from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

/**
 * Lists agent grants the current user has installed and lets them revoke
 * them. Scoped to the current user via Hasura tenant-member permissions —
 * no cross-user visibility. Token columns are NEVER selected here because
 * they're excluded from the tenant-member select_permissions; this is
 * defense in depth, not just a UI choice.
 */
const myAgentGrantsQuery = graphql(`
  query MyAgentGrants($tenantId: uuid!, $userId: uuid!) {
    eluveAgentUserGrants(
      where: {
        tenantId: { _eq: $tenantId }
        userId: { _eq: $userId }
        revokedAt: { _isNull: true }
      }
      orderBy: { grantedAt: DESC }
    ) {
      __typename
      tenantId
      userId
      agentId
      grantedAt
      grantedScopes
      accessTokenExpiresAt
    }
  }
`);

const formatScopes = (scopes: unknown): string[] => {
  if (!Array.isArray(scopes)) return [];
  return scopes.filter((s): s is string => typeof s === 'string');
};

export const AppGrantsSettings: React.FC = () => {
  const { t } = useTranslation();
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const apiClient = useApiClient();
  const apolloClient = useApolloClient();
  const [revokingAgentId, setRevokingAgentId] = useState<string | null>(null);

  const { data } = useSuspenseQuery(myAgentGrantsQuery, {
    variables: { tenantId, userId },
    fetchPolicy: 'cache-and-network',
  });

  const grants = data?.eluveAgentUserGrants ?? [];

  const handleRevoke = async (agentId: string) => {
    setRevokingAgentId(agentId);
    try {
      const res = await apiClient.miniApps.revoke({
        params: { tenantId, agentId },
        body: {},
      });
      if (res.status === 200) {
        toast.success(t('userSettings.appGrants.revoked', { agentId }));
        await apolloClient.refetchQueries({ include: [myAgentGrantsQuery] });
      } else {
        toast.error(t('userSettings.appGrants.revokeFailed'));
      }
    } catch {
      toast.error(t('userSettings.appGrants.revokeFailed'));
    } finally {
      setRevokingAgentId(null);
    }
  };

  if (grants.length === 0) {
    return (
      <Text size="sm" color="tertiary" className="px-6 py-7">
        {t('userSettings.appGrants.empty')}
      </Text>
    );
  }

  return (
    <>
      {grants.map((g, i) => {
        const scopes = formatScopes(g.grantedScopes);
        return (
          <div key={g.agentId}>
            <Stack
              direction="horizontal"
              justify="between"
              align="start"
              gap={4}
              className="px-6 py-7"
            >
              <Stack gap={1} align="start" className="min-w-0">
                <Text size="sm" weight="semibold">
                  {g.agentId}
                </Text>
                <Text size="xs" weight="medium" color="tertiary">
                  {t('userSettings.appGrants.granted', {
                    date: g.grantedAt
                      ? formatDate.toLocalDateTime(g.grantedAt)
                      : t('userSettings.appGrants.unknownDate'),
                  })}
                </Text>
                {scopes.length > 0 && (
                  <ul className="ml-4 list-disc">
                    {scopes.map((s) => (
                      <li key={s}>
                        <Text size="xs" color="tertiary">
                          {s}
                        </Text>
                      </li>
                    ))}
                  </ul>
                )}
              </Stack>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRevoke(g.agentId)}
                loading={revokingAgentId === g.agentId}
                disabled={revokingAgentId !== null}
              >
                {t('userSettings.appGrants.revoke')}
              </Button>
            </Stack>
            {i < grants.length - 1 && <Separator className="my-0" />}
          </div>
        );
      })}
    </>
  );
};
